/**
 * Assessment Session Recovery
 *
 * Persists in-progress assessment state so that a browser refresh
 * during a 15-20 minute test doesn't lose all progress.
 *
 * Flow:
 * 1. On assessment start → saveSessionState()
 * 2. After each answer → saveSessionState() with updated answers
 * 3. On assessment complete/abandon → clearSessionState()
 * 4. On app mount → getRecoverableSession() to check for in-progress test
 * 5. User chooses resume → restore state; or abandon → clearSessionState()
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 * dataLayer migration: D4 (8 april 2026).
 */

import { AssessmentAttempt, AssessmentBlueprint } from '../types'
import { ASSESSMENT_SESSION_MAX_AGE_MS } from '../../config/constants'
import { warn, error as logError, debug } from '../../utils/logger'
import { dataLayer } from '../../data/dataLayer'
import type { FamilyMasteryState } from '../../mastery'

const MAX_SESSION_AGE_MS = ASSESSMENT_SESSION_MAX_AGE_MS

export interface RecoverableSession {
  blueprint: AssessmentBlueprint
  attempt: AssessmentAttempt
  startTime: number
  preFamilyMastery: Record<string, FamilyMasteryState>
  savedAt: number
}

/** Save the current assessment session state for crash recovery. */
export async function saveSessionState(
  blueprint: AssessmentBlueprint,
  attempt: AssessmentAttempt,
  startTime: number,
  preFamilyMastery: Record<string, FamilyMasteryState>,
): Promise<void> {
  try {
    const session: RecoverableSession = {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery,
      savedAt: Date.now(),
    }
    const success = await dataLayer.saveAssessmentSession(
      session as unknown as Record<string, unknown>,
    )
    if (!success) {
      warn('Failed to save assessment session — storage may be full')
    } else {
      debug(
        `Assessment session saved: ${attempt.answers.length}/${blueprint.questions.length} answers`,
      )
    }
  } catch (err) {
    logError('Failed to save assessment session state:', err)
  }
}

/** Check for a recoverable in-progress assessment session. */
export async function getRecoverableSession(): Promise<RecoverableSession | null> {
  try {
    const raw = await dataLayer.getAssessmentSession()
    if (!raw) return null

    const session = raw as unknown as RecoverableSession

    if (Date.now() - session.savedAt > MAX_SESSION_AGE_MS) {
      debug('Discarding stale assessment session (older than 2 hours)')
      await clearSessionState()
      return null
    }

    if (
      !session.blueprint ||
      !session.attempt ||
      !session.attempt.answers ||
      session.attempt.status !== 'in-progress'
    ) {
      debug('Discarding invalid assessment session')
      await clearSessionState()
      return null
    }

    const answeredCount = session.attempt.answers.length
    const totalQuestions = session.blueprint.questions.length
    if (answeredCount >= totalQuestions) {
      debug('Assessment session is already complete — clearing')
      await clearSessionState()
      return null
    }

    debug(
      `Recoverable assessment session found: ${answeredCount}/${totalQuestions} answers, saved ${Math.round((Date.now() - session.savedAt) / 60000)} minutes ago`,
    )
    return session
  } catch (err) {
    logError('Failed to read assessment session:', err)
    await clearSessionState()
    return null
  }
}

/** Clear the saved assessment session state. */
export async function clearSessionState(): Promise<void> {
  try {
    await dataLayer.clearAssessmentSession()
    debug('Assessment session state cleared')
  } catch (err) {
    logError('Failed to clear assessment session state:', err)
  }
}
